import { useEffect, useRef, useState } from "react";
import services from "../data/chatbot/services";
import webProjects from "../data/chatbot/webProjects";

const BOT_DELAY = 700;

// ===============================
// Message Helpers
// ===============================

const createBotMessage = (
    text,
    options = [],
    type = "text",
    projects = []
  ) => ({
    id: Date.now() + Math.random(),
    sender: "bot",
    text,
    options,
    type,
    projects,
  });

const createUserMessage = (text) => ({
  id: Date.now() + Math.random(),
  sender: "user",
  text,
});

export default function useChatbot() {
  // ===============================
  // Chat Messages
  // ===============================

  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);

  // ===============================
  // Conversation State
  // ===============================

  // null | "quote" | "question"
  const [mode, setMode] = useState(null);

  // Quote Steps
  // 0 = inactive
  // 1 = Name
  // 2 = Email
  // 3 = Phone
  // 4 = Service
  // 5 = Requirement
  const [quoteStep, setQuoteStep] = useState(0);

  // Quote Form Data
  const [quoteData, setQuoteData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    requirement: "",
  });

  // Auto Scroll
  const bottomRef = useRef(null);

  // ===============================
  // Reset Conversation
  // ===============================

  const resetConversation = () => {
    setMode(null);
    setQuoteStep(0);

    setQuoteData({
      name: "",
      email: "",
      phone: "",
      service: "",
      requirement: "",
    });
  };

  // ===============================
  // Welcome Message
  // ===============================

  useEffect(() => {
    const welcome = services.welcome;

    setMessages([
      createBotMessage(
        welcome.message,
        welcome.options
      ),
    ]);
  }, []);

  // ===============================
  // Auto Scroll
  // ===============================

  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [messages, typing]);

// ===============================
// Quick Reply Click
// ===============================

const handleOptionClick = (option) => {
    // Show user's selected option
    setMessages((prev) => [
      ...prev,
      createUserMessage(option.label),
    ]);
  
    // Home button
    if (option.next === "welcome") {
      resetConversation();
    }
  
    // Quote Flow
    if (option.next === "quote") {
      resetConversation();
      setMode("quote");
      setQuoteStep(1);
    }
  
    // Question Flow
    if (option.next === "question") {
      resetConversation();
      setMode("question");
    }
  
    setTyping(true);
  
    setTimeout(() => {
        if (option.next === "website") {

            setMessages(prev=>[
                ...prev,
                createBotMessage(
                    "",
                    [
                      {
                        label:"🏠 Back to Home",
                        next:"welcome"
                      }
                    ],
                    "project-slider",
                    webProjects
                )
            ]);
        
            setTyping(false);
        
            return;
        }
      const next = services[option.next];
  
      if (!next) {
        setTyping(false);
        return;
      }
  
      setMessages((prev) => [
        ...prev,
        createBotMessage(
          next.message,
          next.options || []
        ),
      ]);
  
      setTyping(false);
    }, BOT_DELAY);
  };
  // ===============================
// User Message
// ===============================

const sendMessage = (text) => {
    if (!text.trim()) return;
  
    setMessages((prev) => [
      ...prev,
      createUserMessage(text),
    ]);
  
    setTyping(true);
  
    setTimeout(() => {
  
      // ==========================================
      // QUESTION FLOW
      // ==========================================
  
      if (mode === "question") {
  
        setMessages((prev) => [
          ...prev,
          createBotMessage(
            "✅ Thank you for your question!\n\nOur team has received your query and will contact you shortly.",
            [
              {
                label: "🏠 Back to Home",
                next: "welcome",
              },
            ]
          ),
        ]);
  
        resetConversation();
        setTyping(false);
        return;
      }
  
      // ==========================================
      // QUOTE FLOW
      // ==========================================
  
      if (mode === "quote") {
  
        switch (quoteStep) {
  
          case 1:
  
            setQuoteData((prev) => ({
              ...prev,
              name: text,
            }));
  
            setQuoteStep(2);
  
            setMessages((prev) => [
              ...prev,
              createBotMessage(
                "📧 Great! Please enter your Email Address."
              ),
            ]);
  
            setTyping(false);
            return;
  
          // ----------------------------
          // Step 2 - Email
          // ----------------------------
  
          case 2: {

            const emailRegex =
              /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          
            if (!emailRegex.test(text.trim())) {
          
              setMessages((prev) => [
                ...prev,
                createBotMessage(
                  "❌ Please enter a valid Email Address.\n\nExample: abc@gmail.com"
                ),
              ]);
          
              setTyping(false);
              return;
            }
          
            setQuoteData((prev) => ({
              ...prev,
              email: text.trim(),
            }));
          
            setQuoteStep(3);
          
            setMessages((prev) => [
              ...prev,
              createBotMessage(
                "📱 Please enter your 10-digit Phone Number."
              ),
            ]);
          
            setTyping(false);
            return;
          }
  
            setQuoteData((prev) => ({
              ...prev,
              email: text,
            }));
  
            setQuoteStep(3);
  
            setMessages((prev) => [
              ...prev,
              createBotMessage(
                "📱 Please enter your Phone Number."
              ),
            ]);
  
            setTyping(false);
            return;
  
          // ----------------------------
          // Step 3 - Phone
          // ----------------------------
  
          case 3: {

            const phone = text.trim();
          
            if (!/^\d{10}$/.test(phone)) {
          
              setMessages((prev) => [
                ...prev,
                createBotMessage(
                  "❌ Please enter a valid 10-digit Mobile Number.\n\nExample: 9876543210"
                ),
              ]);
          
              setTyping(false);
              return;
            }
          
            setQuoteData((prev) => ({
              ...prev,
              phone,
            }));
          
            setQuoteStep(4);
          
            setMessages((prev) => [
              ...prev,
              createBotMessage(
                "💼 Which service are you interested in?\n\nExamples:\n• AI Solutions\n• Website Development\n• App Development\n• Digital Marketing\n• Branding & Design\n• Custom Software"
              ),
            ]);
          
            setTyping(false);
            return;
          }
          // ----------------------------
          // Step 4 - Service
          // ----------------------------
  
          case 4:
  
            setQuoteData((prev) => ({
              ...prev,
              service: text,
            }));
  
            setQuoteStep(5);
  
            setMessages((prev) => [
              ...prev,
              createBotMessage(
                "📝 Please describe your project requirements."
              ),
            ]);
  
            setTyping(false);
            return;
  
          // ----------------------------
          // Step 5 - Requirement
          // ----------------------------
  
          case 5:
  
            setQuoteData((prev) => ({
              ...prev,
              requirement: text,
            }));
  
            setMessages((prev) => [
              ...prev,
              createBotMessage(
                "🎉 Thank you for sharing your requirements!\n\nOur team has received your request and will contact you shortly.\n\nHave a great day 😊",
                [
                  {
                    label: "🏠 Back to Home",
                    next: "welcome",
                  },
                  {
                    label: "📞 Contact Us",
                    next: "contact",
                  },
                ]
              ),
            ]);
  
            resetConversation();
  
            setTyping(false);
            return;
  
          default:
  
            resetConversation();
  
            setTyping(false);
            return;
        }
      }
      // ==========================================
    // NORMAL CHATBOT (Keyword Search)
    // ==========================================

    const lower = text.toLowerCase();

    let response = services.fallback;

    if (
      lower.includes("ai") ||
      lower.includes("chatbot") ||
      lower.includes("agent") ||
      lower.includes("automation")
    ) {
      response = services.ai;
    } else if (
      lower.includes("website") ||
      lower.includes("web") ||
      lower.includes("react") ||
      lower.includes("wordpress")
    ) {
      response = services.web;
    } else if (
      lower.includes("app") ||
      lower.includes("android") ||
      lower.includes("ios") ||
      lower.includes("mobile")
    ) {
      response = services.app;
    } else if (
      lower.includes("marketing") ||
      lower.includes("seo") ||
      lower.includes("google ads") ||
      lower.includes("meta ads")
    ) {
      response = services.marketing;
    } else if (
      lower.includes("branding") ||
      lower.includes("logo") ||
      lower.includes("design")
    ) {
      response = services.branding;
    } else if (
      lower.includes("software") ||
      lower.includes("crm") ||
      lower.includes("erp")
    ) {
      response = services.software;
    } else if (
      lower.includes("contact") ||
      lower.includes("phone") ||
      lower.includes("email")
    ) {
      response = services.contact;
    } else if (
      lower.includes("quote") ||
      lower.includes("price") ||
      lower.includes("cost")
    ) {
      response = services.quote;
    }

    setMessages((prev) => [
      ...prev,
      createBotMessage(
        response.message,
        response.options || []
      ),
    ]);

    setTyping(false);

  }, BOT_DELAY);
};

// ==========================================
// Return
// ==========================================

return {
  messages,
  typing,
  sendMessage,
  handleOptionClick,
  bottomRef,
};
}
  